// client/src/pages/MFiles2PEReconReport.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare } from 'lucide-react';
import DataTable from '../components/DataTable';
import type { TableColumn } from 'react-data-table-component';

// ─── Constants ───────────────────────────────────────────────────────────────

const REPORT_KEY = 'mfiles2pe-recon';
const HIDDEN_COLUMNS = new Set(['id', 'Job_Idx']);
const BLOB_PATH = 'htmlData/adminHq/reports/mFiles2PERecon/masterAudit.json';
const BLOB_POLL_MS = 5 * 60 * 1000;   // 5 minutes
const REVIEW_POLL_MS = 30 * 1000;      // 30 seconds

// ─── Types ───────────────────────────────────────────────────────────────────

type ReconRow = Record<string, unknown> & { Job_Idx?: string | number };

type BlobPayload = {
  refreshDate: string;
  reportBody: ReconRow[];
};

type ReviewRecord = {
  row_key: string;
  reviewed_by: string;
  reviewed_at: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function norm(s: unknown): string {
  return (s ?? '').toString().toLowerCase().trim();
}

/** Convert a UTC ISO string to US Central Time display string */
function utcToCentral(utcStr: string): string {
  try {
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return utcStr;
    return d.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  } catch {
    return utcStr;
  }
}

// ─── Data fetching ───────────────────────────────────────────────────────────

async function fetchBlobData(): Promise<BlobPayload> {
  const resp = await fetch(`/api/report-blob?path=${encodeURIComponent(BLOB_PATH)}`, {
    credentials: 'include',
    cache: 'no-store',
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to load report data (HTTP ${resp.status}). ${text}`);
  }
  return resp.json();
}

async function fetchReviews(): Promise<ReviewRecord[]> {
  const resp = await fetch(`/api/audit-reviews?reportKey=${encodeURIComponent(REPORT_KEY)}`, {
    credentials: 'include',
    cache: 'no-store',
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.reviews || [];
}

async function postReview(rowKey: string): Promise<void> {
  const resp = await fetch('/api/audit-review', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reportKey: REPORT_KEY, rowKey: String(rowKey) }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to mark reviewed (HTTP ${resp.status}). ${text}`);
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function MFiles2PEReconReport() {
  const navigate = useNavigate();

  const [rows, setRows]               = useState<ReconRow[]>([]);
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const [refreshDate, setRefreshDate]   = useState<string>('');
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [search, setSearch]             = useState('');
  const [markingRow, setMarkingRow]     = useState<string | null>(null);

  const blobIntervalRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const reviewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Initial load ──────────────────────────────────────────────────────────

  const loadBlob = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const data = await fetchBlobData();
      setRows(Array.isArray(data.reportBody) ? data.reportBody : []);
      setRefreshDate(data.refreshDate || '');
      setError(null);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const loadReviews = useCallback(async () => {
    try {
      const reviews = await fetchReviews();
      setReviewedKeys(new Set(reviews.map(r => String(r.row_key))));
    } catch {
      // silent — review polling is best-effort
    }
  }, []);

  useEffect(() => {
    // Initial fetch of both blob + reviews
    Promise.all([loadBlob(true), loadReviews()]);

    // Poll blob every 5 minutes
    blobIntervalRef.current = setInterval(() => loadBlob(false), BLOB_POLL_MS);
    // Poll reviews every 30 seconds
    reviewIntervalRef.current = setInterval(loadReviews, REVIEW_POLL_MS);

    return () => {
      if (blobIntervalRef.current) clearInterval(blobIntervalRef.current);
      if (reviewIntervalRef.current) clearInterval(reviewIntervalRef.current);
    };
  }, [loadBlob, loadReviews]);

  // ── Mark reviewed handler ─────────────────────────────────────────────────

  const handleMarkReviewed = useCallback(async (rowKey: string) => {
    setMarkingRow(rowKey);
    // Optimistic: remove immediately
    setReviewedKeys(prev => new Set([...prev, rowKey]));
    try {
      await postReview(rowKey);
    } catch {
      // Rollback on failure
      setReviewedKeys(prev => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    } finally {
      setMarkingRow(null);
    }
  }, []);

  // ── Filtered rows (exclude reviewed + search) ────────────────────────────

  const filtered = useMemo(() => {
    const q = norm(search);
    return rows.filter(r => {
      const key = String(r.Job_Idx ?? '');
      if (reviewedKeys.has(key)) return false;
      if (q) {
        const haystack = Object.values(r).map(v => norm(v)).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, reviewedKeys, search]);

  // ── Build columns dynamically from first row ─────────────────────────────

  const columns: TableColumn<ReconRow>[] = useMemo(() => {
    if (rows.length === 0) return [];

    const sampleRow = rows[0];
    const dataColumns: TableColumn<ReconRow>[] = Object.keys(sampleRow)
      .filter(key => !HIDDEN_COLUMNS.has(key))
      .map(key => ({
        name: key,
        selector: (row: ReconRow) => {
          const v = row[key];
          return v == null ? '' : String(v);
        },
        sortable: true,
        wrap: true,
        minWidth: '120px',
      }));

    // Append the "Mark Reviewed" checkbox column
    const reviewColumn: TableColumn<ReconRow> = {
      name: 'Mark Reviewed',
      cell: (row: ReconRow) => {
        const key = String(row.Job_Idx ?? '');
        const isMarking = markingRow === key;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleMarkReviewed(key); }}
            disabled={isMarking}
            title="Mark as reviewed"
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 4,
              color: isMarking ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
              cursor: isMarking ? 'wait' : 'pointer',
              padding: '4px 10px',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              transition: 'all .15s',
            }}
            onMouseEnter={e => { if (!isMarking) e.currentTarget.style.borderColor = '#57d38c'; e.currentTarget.style.color = '#57d38c'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
          >
            <CheckSquare size={14} />
            {isMarking ? 'Saving…' : 'Reviewed'}
          </button>
        );
      },
      width: '140px',
      right: true,
    };

    return [...dataColumns, reviewColumn];
  }, [rows, markingRow, handleMarkReviewed]);

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn-ghost" onClick={() => navigate('/reports')} style={{ padding: '6px 8px' }}>
            <ArrowLeft size={16} />
          </button>
          <div style={{ fontSize: 20, fontWeight: 700 }}>M-Files to PE Reconciliation</div>
        </div>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse" style={{ height: 48, borderRadius: 8, animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn-ghost" onClick={() => navigate('/reports')} style={{ padding: '6px 8px' }}>
            <ArrowLeft size={16} />
          </button>
          <div style={{ fontSize: 20, fontWeight: 700 }}>M-Files to PE Reconciliation</div>
        </div>
        <div className="card" style={{ padding: 20, color: '#f87171' }}>{error}</div>
        <button className="btn-ghost" onClick={() => loadBlob(true)} style={{ alignSelf: 'flex-start', gap: 6 }}>
          <RotateCcw size={14} /> Retry
        </button>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn-ghost" onClick={() => navigate('/reports')} style={{ padding: '6px 8px' }}>
            <ArrowLeft size={16} />
          </button>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>M-Files to PE Reconciliation</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {refreshDate && (
            <span className="muted" style={{ fontSize: 12 }}>
              Data refreshed: {utcToCentral(refreshDate)}
            </span>
          )}
          <button className="btn-ghost" onClick={() => loadBlob(true)} title="Refresh" style={{ padding: '6px 8px' }}>
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {/* Search + count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input"
          style={{ minWidth: 220, maxWidth: 320, fontSize: 13 }}
        />
        <span className="muted" style={{ fontSize: 12 }}>
          {filtered.length} row{filtered.length !== 1 ? 's' : ''} remaining
          {reviewedKeys.size > 0 && ` · ${reviewedKeys.size} reviewed`}
        </span>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
            No mismatches found that haven't already been reviewed.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            pagination
            paginationPerPage={50}
            paginationRowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </div>
    </div>
  );
}
